import { ButtonERP, CalendarPrimeReact } from "@finanedi.es/storybook"
import {
  faArrowLeft,
  faFileLines,
  faRotateRight,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useQuery } from "@tanstack/react-query"
import { getSaldosCuentaBancaria } from "api/CuentaBancaria.service"
import { Skeleton } from "primereact/skeleton"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation, useParams } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { frontendCurrency } from "utils/formatCurrency"
import { backendDateFormat } from "utils/formatDate"
import { SaldosTeoricos } from "../Conciliaciones"

interface HeaderProps {
  date: [Date, Date | null]
  setDate: $TSMigrationFixMe
  refrescarTablas: () => void
  saldosTeoricos: SaldosTeoricos
  setSaldosTeoricos: React.Dispatch<React.SetStateAction<SaldosTeoricos>>
}

const Header: React.FC<HeaderProps> = ({
  date,
  setDate,
  refrescarTablas,
  saldosTeoricos,
  setSaldosTeoricos,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const history = useHistory()
  const { state } = useLocation()
  const { id: paramId = "" } = useParams<Record<"id", string>>()

  const {
    data: saldos,
    isLoading: saldosIsLoading,
    refetch: saldosRefetch,
    isFetching: saldosIsFetching,
  } = useQuery({
    queryKey: ["getSaldosCuentaBancaria", paramId, date, date[1]],
    queryFn: () =>
      getSaldosCuentaBancaria(paramId, {
        FechaUpperLimit: backendDateFormat(date[1] ? date[1] : new Date()),
      }),
    enabled: date[1] !== null,
    refetchOnWindowFocus: false,
    retry: false,
  })

  useEffect(() => {
    if (!saldosIsLoading) {
      setSaldosTeoricos({
        saldoTeoricoBancario: saldos?.saldoBancario ?? 0,
        saldoTeoricoContable: saldos?.saldoContable ?? 0,
        diferencia: saldos?.diferencia ?? 0,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saldos])

  const handlePropuestasConciliacion = () => {
    history.push({
      pathname: `${ROUTES.PROPUESTAS_CONCILIACION}/${paramId}`,
      search: `?iban=${encodeURIComponent(
        state?.iban ?? ""
      )}&nifTitular=${encodeURIComponent(state?.nifTitular ?? "")}`,
    })
  }

  return (
    <header className='tw-flex tw-flex-col tw-items-center tw-justify-between tw-rounded-t-xl tw-bg-white !tw-px-4 tw-pb-4 tw-pt-6 tw-shadow-3xl xl:tw-flex-row'>
      <div className='tw-flex tw-flex-row tw-items-center tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-nowrap !tw-w-fit'>
        <div className='tw-flex tw-flex-row tw-items-center tw-gap-x-4'>
          <ButtonERP
            customize={{
              type: "white",
              size: "md",
            }}
            icon={<FontAwesomeIcon icon={faArrowLeft} />}
            onClick={() => history.goBack()}
          />

          <span className='tw-w-full tw-flex-1 tw-overflow-hidden tw-truncate tw-text-left tw-text-xl tw-font-medium'>
            {`${t("bancos.conciliacion.title")} ${
              state?.entidadBancaria ? `- ${state.entidadBancaria}` : ""
            }`}
          </span>
        </div>
      </div>
      <div className='tw-flex tw-flex-col lg:tw-flex-row tw-items-center xl:tw-ml-6 tw-mt-0 '>
        <div className='tw-flex tw-flex-row tw-items-center tw-mt-6 lg:tw-mt-0 tw-gap-4'>
          <ButtonERP
            customize={{
              type: "white",
              size: "md",
            }}
            onClick={() => {
              saldosRefetch()
              refrescarTablas()
            }}
            icon={<FontAwesomeIcon icon={faRotateRight} />}
            loading={saldosIsFetching}
          />
          <ButtonERP
            customize={{
              type: "white",
              size: "md",
            }}
            tooltip={t("bancos.bankCard.propuestasConciliacionButton") || ""}
            icon={<FontAwesomeIcon icon={faFileLines} />}
            onClick={() => handlePropuestasConciliacion()}
          />
          <div className='tw-ml-6'>
            <div className='tw-relative !tw-w-fit !tw-h-10 tw-bg-white'>
              <CalendarPrimeReact
                value={date}
                selectionMode='range'
                locale={language}
                dateFormat='dd/mm/yy'
                id='calendar-input'
                inputClassName='!tw-text-white !tw-text-base !tw-w-52 !tw-h-10 !tw-bg-transparent !tw-py-0 tw-flex tw-items-center tw-cursor-pointer !tw-border-primary hover:!tw-border-primary'
                className='!tw-h-10 tw-bg-primary tw-rounded-md !tw-border-primary'
                onChange={({ value }) => {
                  setDate(value)
                }}
                onHide={() => {
                  if (!date[1]) {
                    setDate([
                      date[0],
                      date[0] <= new Date() ? new Date() : date[0],
                    ])
                  }
                }}
                readOnlyInput
                showIcon
                hideOnRangeSelection
              />
            </div>
          </div>
        </div>
        <div className='tw-bg-background tw-px-6 tw-py-1 tw-h-14 tw-flex tw-flex-row tw-gap-6 tw-items-center tw-mt-6 lg:tw-mt-0 lg:tw-ml-6'>
          <div className='tw-flex tw-flex-col tw-flex-nowrap tw-min-w-44 tw-justify-between'>
            <div className='tw-flex tw-flex-row tw-justify-between tw-w-full tw-h-full tw-items-end'>
              <span className='tw-text-nowrap tw-mr-2 tw-text-sm'>
                {t("bancos.conciliacion.saldoBank")}
              </span>
              {saldosIsLoading ? (
                <Skeleton className='tw-w-full tw-rounded-md !tw-h-7' />
              ) : (
                <span className='tw-font-medium tw-whitespace-nowrap tw-inline-flex'>
                  {frontendCurrency(saldos?.saldoBancario ?? 0)}
                </span>
              )}
            </div>
            <div className='tw-flex tw-flex-row tw-justify-between tw-w-full tw-h-full tw-items-end'>
              <span className='tw-text-nowrap tw-mr-2 tw-text-sm'>
                {t("bancos.conciliacion.theoreticalBalance")}
              </span>
              {saldosIsLoading ? (
                <Skeleton className='tw-w-full tw-rounded-md !tw-h-7' />
              ) : (
                <span className='tw-font-medium tw-whitespace-nowrap tw-inline-flex'>
                  {frontendCurrency(saldosTeoricos.saldoTeoricoBancario ?? 0)}
                </span>
              )}
            </div>
          </div>
          <div className='tw-flex tw-flex-col tw-flex-nowrap tw-items-end tw-min-w-44 tw-justify-between'>
            <div className='tw-flex tw-flex-row tw-justify-between tw-w-full tw-h-full tw-items-end'>
              <span className='tw-text-nowrap tw-mr-2 tw-text-sm'>
                {t("bancos.conciliacion.saldoContable")}
              </span>
              {saldosIsLoading ? (
                <Skeleton className='tw-w-full tw-rounded-md !tw-h-7' />
              ) : (
                <span className='tw-font-medium tw-whitespace-nowrap tw-inline-flex'>
                  {frontendCurrency(saldos?.saldoContable ?? 0)}
                </span>
              )}
            </div>
            <div className='tw-flex tw-flex-row tw-justify-between tw-w-full tw-h-full tw-items-end'>
              <span className='tw-text-nowrap tw-mr-2 tw-text-sm'>
                {t("bancos.conciliacion.theoreticalBalance")}
              </span>
              {saldosIsLoading ? (
                <Skeleton className='tw-w-full tw-rounded-md !tw-h-7' />
              ) : (
                <span className='tw-font-medium tw-whitespace-nowrap tw-inline-flex'>
                  {frontendCurrency(saldosTeoricos?.saldoTeoricoContable ?? 0)}
                </span>
              )}
            </div>
          </div>
          <div className='tw-flex tw-flex-col tw-flex-nowrap tw-items-end tw-min-w-44 tw-justify-between'>
            <div className='tw-flex tw-flex-row tw-justify-between tw-w-full tw-h-full tw-items-end'>
              <span className='tw-text-nowrap tw-mr-2 tw-text-sm'>
                {t("bancos.conciliacion.saldoDiferencia")}
              </span>
              {saldosIsLoading ? (
                <Skeleton className='tw-w-full tw-rounded-md !tw-h-7' />
              ) : (
                <span
                  className={`tw-font-medium ${
                    saldos?.diferencia === undefined || saldos?.diferencia === 0
                      ? "tw-text-text"
                      : saldos?.diferencia > 0
                      ? "tw-text-secondary"
                      : "tw-text-danger"
                  } tw-whitespace-nowrap tw-inline-flex tw-ml-1`}
                >
                  {frontendCurrency(saldos?.diferencia ?? 0)}
                </span>
              )}
            </div>
            <div className='tw-flex tw-flex-row tw-justify-between tw-w-full tw-h-full tw-items-end'>
              <span className='tw-text-nowrap tw-mr-2 tw-text-sm'>
                {t("bancos.conciliacion.saldoDiferencia")}
              </span>
              {saldosIsLoading ? (
                <Skeleton className='tw-w-full tw-rounded-md !tw-h-7' />
              ) : (
                <span
                  className={`tw-font-medium ${
                    saldos?.diferencia === undefined || saldos?.diferencia === 0
                      ? "tw-text-text"
                      : saldos?.diferencia > 0
                      ? "tw-text-secondary"
                      : "tw-text-danger"
                  } tw-whitespace-nowrap tw-inline-flex tw-ml-1`}
                >
                  {frontendCurrency(saldosTeoricos.diferencia ?? 0)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
